import ApiTable from "components/ApiTable";
import { FIRST_PAGE, PAGE_SIZE, pageSizeOptions } from "helpers/values";
import React, { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  segmentationOptions,
  segmentationTableColumnsAll,
  segmentationTableColumnsSimplified,
} from "utils/reposts";
import Dropdown from "../../components/Dropdown";
import { State } from "../../state";
import {
  fetchSegmentationFilters,
  fetchSegmentationInfo,
  updateSegmentationType,
} from "../../state/segmentation-report";
import { SORTING_ORDER } from "enum/sorting.enum";

type ReduxProps = ConnectedProps<typeof connecter> & RouteComponentProps;
type SegmentationReportProps = ReduxProps & {
  selectedProjectId: number;
  selectedStores: number[];
  projectId: number;
};

const controllersStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "12px",
  height: "100%",
  minHeight: "100%",
  justifyContent: "space-between",
  alignItems: "center",
};

const lineSeparatorCss: React.CSSProperties = {
  height: "1px",
  width: "100%",
  backgroundColor: "#53598D26",
  margin: "5px auto",
};

const reportContainerCss: React.CSSProperties = {
  height: "400px",
  overflow: "auto",
};

const chartContentCSS: React.CSSProperties = {
  display: "flex",
  width: "100%",
};

const controllerDropdownStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
};

interface SegmentationReportState {
  search: string;
  size: number;
  page: number;
  sort: string;
}

class SegmentationReport extends Component<SegmentationReportProps, SegmentationReportState> {
  constructor(props: SegmentationReportProps) {
    super(props);
    this.state = {
      search: "",
      size: PAGE_SIZE,
      page: FIRST_PAGE,
      sort: ''
    };
  }
  async componentDidMount() {
    const { projectId, selectedProjectId, selectedStores } = this.props;
    this.loadProjectData(projectId, selectedProjectId, selectedStores);
  }

  loadProjectData = async (projectId: number, selectedProjectId: number, storeIds: number[]) => {
    try {
      this.props.fetchSegmentationInfo({
        id: projectId,
        comparisonId: selectedProjectId,
        segmentationType: this.props.segmentationType,
        storeIds,
        page: FIRST_PAGE,
        size: PAGE_SIZE,
        search: this.state.search,
        sort: this.state.sort
      });
    } catch (error) {
      console.error("Error loading project data:", error);
    }
  };

  async componentDidUpdate(prevProps: SegmentationReportProps) {
    const { projectId, selectedProjectId, selectedStores } = this.props;
    const projectChanged = prevProps.projectId !== projectId;
    const selectedChanged = prevProps.selectedProjectId !== selectedProjectId;
    const storesChanged = JSON.stringify(prevProps.selectedStores) !== JSON.stringify(selectedStores);
    if (projectChanged || selectedChanged || storesChanged) {
      await this.loadProjectData(projectId, selectedProjectId, selectedStores);
    }
  }
  handlePageChange = (page: number, size: number) => {
    const { projectId, selectedProjectId, selectedStores } = this.props;
    // Guard: don't call API if already on this page
    if (page === this.state.page && size === this.state.size) return;
    this.props.fetchSegmentationInfo({
      id: projectId,
      comparisonId: selectedProjectId,
      segmentationType: this.props.segmentationType,
      storeIds: selectedStores,
      page: page,
      size: size,
      search: this.state.search,
      sort: this.state.sort
    });
    this.setState({ page, size });
  };

  render() {
    const { selectedProjectId, content, tableLoading, segmentationType } = this.props;
    const { page, size } = this.state;
    let segmentationTableData: any = [];
    if (content && content["segmentationReport"]) {
      segmentationTableData = content["segmentationReport"];
    }

    const transformedOptions = segmentationOptions.map(opt => ({
      ...opt,
      label: opt.label.replace(/_/g, ' ')
    }));
    
    const handleSegmentTypesChange = (value: string) => {
      this.props.updateSegmentationType(value);
      const { projectId, selectedProjectId, selectedStores } = this.props;
      this.setState({ page: FIRST_PAGE });
      this.props.fetchSegmentationInfo({
        id: projectId,
        comparisonId: selectedProjectId,
        segmentationType: value,
        storeIds: selectedStores,
        page: FIRST_PAGE,
        size: this.state.size,
        search: this.state.search,
        sort: this.state.sort
      });
    };

    const columnName = {
      header: transformedOptions.find((opt) => opt.value === segmentationType)?.label,
      field: "segment",
    };

    const segmentationTableColumns = [
      columnName,
      ...(selectedProjectId < 0 ? segmentationTableColumnsSimplified : segmentationTableColumnsAll),
    ];

    return (
      <div className="reset-recap-container">
        <style>
          {`
            .chart-styles table thead tr th {
                position: sticky !important;
                top: 0 !important;
                z-index: 1;
                background-color: #F9FAFB;
            }
            .chart-styles > div > div {
                max-height: calc(100vh - 330px) !important;
            }
            .chart-styles table tbody tr td {
                height: 45px !important;
                vertical-align: middle;
                min-width: 100px;
            }
          `}
        </style>
        <div className="store-summary-header">
          <div style={controllersStyle}>
            <div>
              Segmentation Report
              {/* <div className="reset-recap-sub-header">Highlights space efficiency to inform portfolio and space allocation decisions.</div> */}
            </div>
            <div className="segmentation-filter" style={controllerDropdownStyle}>
              <div style={{ marginRight: "8px", width: "200px" }}>
                <div className="header-controller-dropdown">Select Segmentation</div>
                <div>
                  <Dropdown
                    value={
                      transformedOptions.find((opt) => opt.value === segmentationType) ?? null
                    }
                    options={transformedOptions}
                    onChange={(selected) => handleSegmentTypesChange(selected.value)}
                    placeholder="Select Types"
                    isSearchable
                    controlStyles={{ minHeight: 34, borderRadius: "16px" }}
                    noShadow={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={lineSeparatorCss} />
        <div className="reset-recap-chart-container" style={reportContainerCss}>
          <div className="charts-row" style={chartContentCSS}>
            <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
              {/* <div className="chart-headers">Segmentation Report</div>
              <div className="chart-sub-header">
                Highlights space efficiency to inform portfolio and space allocation decisions.
              </div> */}
              <ApiTable
                data={segmentationTableData}
                columns={segmentationTableColumns}
                total={content.count}
                page={page}
                pageSize={size}
                pageSizeOptions={pageSizeOptions}
                loading={tableLoading}
                onSearch={(field: string, value: string) => {
                  this.setState(
                    (prevState) => ({
                      ...prevState,
                      page: FIRST_PAGE,
                      search: value,
                    }),
                    () => {
                      const { projectId, selectedProjectId, selectedStores } = this.props;
                      this.props.fetchSegmentationInfo({
                        id: projectId,
                        comparisonId: selectedProjectId,
                        segmentationType: this.props.segmentationType,
                        storeIds: selectedStores,
                        page: FIRST_PAGE,
                        size: this.state.size,
                        search: value,
                        sort: this.state.sort
                      });
                    }
                  );
                }}
                onSort={(field: string, order: SORTING_ORDER) => {
                  const sortParam = `${field}.${order}`;
                  this.setState(
                    (prevState) => ({
                      ...prevState,
                      sort: sortParam
                    }),
                    () => {
                      const { size = PAGE_SIZE, page = FIRST_PAGE  } = this.state;
                      const { projectId, selectedStores  } = this.props;
                      this.props.fetchSegmentationInfo({
                        id: projectId,
                        comparisonId: selectedProjectId,
                        segmentationType: this.props.segmentationType,
                        storeIds: selectedStores,
                        page,
                        size,
                        search: this.state.search,
                        sort: sortParam
                      });
                    }
                  );
                }}
                onPageChange={this.handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    filters: state.segmentationDetails.filters,
    content: state.segmentationDetails.content,
    comparisonData: state.segmentationDetails.comparisonData,
    loading: state.segmentationDetails.loading,
    tableLoading: state.segmentationDetails.tableLoading,
    segmentationType: state.segmentationDetails.segmentationType,
  }),
  {
    fetchSegmentationFilters,
    fetchSegmentationInfo,
    updateSegmentationType,
  }
);

export default connecter(withRouter(SegmentationReport));
